import { Shield, FileText, CreditCard, AlertTriangle, Home, Scale } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import SiteNav from "../components/Layout/SiteNav";

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      <div className="container mx-auto max-w-4xl px-4 py-12">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold">Terms of Service</h1>
          <p className="text-muted-foreground">
            Please read these terms carefully before using Muslim+
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Last updated: March 4, 2026
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general" className="gap-2">
              <Scale className="h-4 w-4" />
              General Terms
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="acceptable" className="gap-2">
              <FileText className="h-4 w-4" />
              Acceptable Use
            </TabsTrigger>
          </TabsList>

          {/* General Terms Tab */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">
                  General Terms of Service
                </CardTitle>
                <CardDescription>
                  These Terms of Service ("Terms") govern your access to and use
                  of the Muslim+ platform, including our website, applications,
                  and services (collectively, the "Service"). By accessing or
                  using the Service, you agree to be bound by these Terms.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    1. Acceptance of Terms
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    By creating an account or using Muslim+, you acknowledge that
                    you have read, understood, and agree to be bound by these
                    Terms and our Privacy Policy. If you do not agree with any
                    part of these Terms, you must not use our Service. You must
                    be at least 18 years old, or the age of legal majority in
                    your jurisdiction, to create an account and use the Service.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    2. Description of Service
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Muslim+ is a community platform that provides prayer times,
                    masjid directories, event listings, and halal business
                    directory services. We offer both free community features and
                    paid subscription plans for businesses ("Halal Business
                    Listings"). The Service is provided "as is" and "as
                    available" without warranties of any kind, either express or
                    implied.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    3. Account Registration
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    When you create an account, you agree to provide accurate,
                    current, and complete information. You are responsible for
                    maintaining the confidentiality of your account credentials
                    and for all activities that occur under your account. You
                    must notify us immediately of any unauthorized use of your
                    account. Muslim+ reserves the right to suspend or terminate
                    accounts that violate these Terms.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    4. Intellectual Property
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    All content, features, and functionality of the Service,
                    including but not limited to text, graphics, logos, and
                    software, are owned by Muslim+ or its licensors and are
                    protected by copyright, trademark, and other intellectual
                    property laws. You may not reproduce, distribute, modify, or
                    create derivative works from any content on the Service
                    without prior written consent.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    5. Limitation of Liability
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    To the maximum extent permitted by applicable law, Muslim+
                    and its officers, directors, employees, and agents shall not
                    be liable for any indirect, incidental, special,
                    consequential, or punitive damages, including loss of
                    profits, data, use, or goodwill, arising out of or related
                    to your use of the Service. Our total liability for any
                    claims arising from your use of the Service shall not exceed
                    the amount you paid us in the twelve (12) months preceding
                    the claim.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    6. Indemnification
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    You agree to indemnify, defend, and hold harmless Muslim+,
                    its affiliates, and their respective officers, directors,
                    employees, and agents from and against any claims,
                    liabilities, damages, losses, and expenses arising out of or
                    related to your use of the Service, your violation of these
                    Terms, or your violation of any rights of a third party.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    7. Governing Law
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    These Terms shall be governed by and construed in accordance
                    with the laws of the State of Oregon, United States, without
                    regard to its conflict of law provisions. Any disputes
                    arising under these Terms shall be resolved in the state or
                    federal courts located in Multnomah County, Oregon.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    8. Modifications to Terms
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    We reserve the right to modify these Terms at any time. We
                    will notify you of material changes by posting the updated
                    Terms on our website and updating the "Last updated" date.
                    Your continued use of the Service after any changes
                    constitutes acceptance of the revised Terms. For subscribers
                    with active paid plans, material changes to subscription
                    terms will be communicated via email at least 30 days in
                    advance.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    9. Contact Information
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    For questions about these Terms, please contact us at{" "}
                    <a
                      href="mailto:support@muslimplus.org"
                      className="text-primary hover:underline"
                    >
                      support@muslimplus.org
                    </a>
                    .
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Terms Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">
                  Subscription & Billing Terms
                </CardTitle>
                <CardDescription>
                  These terms apply specifically to paid Halal Business Listing
                  subscriptions on Muslim+.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    1. Subscription Plans
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Muslim+ offers monthly subscription plans for halal
                    businesses: a Basic plan and a Pro plan. The features and
                    pricing of each plan are described on the subscription
                    signup page. We reserve the right to modify plan pricing
                    and features with 30 days' advance notice to active
                    subscribers.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    2. Billing & Payment
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Subscriptions are billed monthly on a recurring basis. By
                    providing a payment method, you authorize Muslim+ to charge
                    your payment method for all fees incurred. Payment
                    processing is handled securely by Stripe, Inc. Muslim+ does
                    not store your full credit card details on our servers. You
                    are responsible for keeping your payment information
                    current. If a payment fails, we may suspend your listing
                    until payment is received.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    3. Application Review & Approval
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    All halal business listing applications are subject to
                    review and approval by Muslim+ administrators. During the
                    application process, you will provide your payment method,
                    but you will not be charged until your application is
                    approved. If your application is rejected, your payment
                    method will not be charged. We aim to review applications
                    within 5-7 business days but do not guarantee specific
                    review timelines.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    4. Halal Certification Requirements
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Businesses must provide valid halal certification
                    documentation as part of the application process. Muslim+
                    reviews submitted certifications but does not independently
                    verify halal compliance. The business owner is solely
                    responsible for maintaining valid halal certification and
                    ensuring the accuracy of all submitted information.
                    Providing false or misleading certification may result in
                    immediate termination of your listing without refund.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    5. Cancellation Policy
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    You may cancel your subscription at any time by contacting
                    us at{" "}
                    <a
                      href="mailto:support@muslimplus.org"
                      className="text-primary hover:underline"
                    >
                      support@muslimplus.org
                    </a>
                    . Upon cancellation, your listing will remain active and
                    you will continue to have full access to all features of
                    your plan until the end of your current billing cycle.
                    After your billing cycle ends, your listing will be
                    removed from the directory and access to paid features
                    will be discontinued. No further charges will be made
                    after cancellation.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    6. No Refunds
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    All subscription payments are final and non-refundable.
                    Muslim+ does not offer refunds for any reason, including
                    partial months of service, unused features, or early
                    cancellation. When you cancel, you retain access to your
                    plan for the remainder of the billing cycle you have
                    already paid for, but no refund will be issued for that
                    period. By subscribing, you acknowledge and agree to this
                    no-refund policy.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    7. Termination by Muslim+
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Muslim+ reserves the right to suspend or terminate your
                    subscription and remove your listing at any time if you
                    violate these Terms, provide false information, fail to
                    maintain valid halal certification, or engage in any
                    activity that we deem harmful to the platform or its users.
                    In such cases, no refund will be provided. We will make
                    reasonable efforts to notify you before termination, except
                    where immediate action is necessary.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    8. Service Level
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    Muslim+ strives to maintain the availability and
                    performance of the Service but does not guarantee uninterrupted
                    access. We are not liable for temporary downtime due to
                    maintenance, updates, or circumstances beyond our
                    reasonable control. Scheduled maintenance windows will be
                    communicated in advance when possible.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Acceptable Use Tab */}
          <TabsContent value="acceptable">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Acceptable Use Policy</CardTitle>
                <CardDescription>
                  This policy outlines what is and is not permitted when using
                  Muslim+.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    1. Permitted Use
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    You may use Muslim+ for its intended purposes: finding
                    prayer times and masjid information, discovering community
                    events, browsing halal business listings, and managing your
                    own business listing if you are a subscriber. All use must
                    comply with applicable local, state, federal, and
                    international laws and regulations.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    2. Prohibited Activities
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    You may not use the Service to: submit false, misleading, or
                    fraudulent information; impersonate another person or
                    entity; scrape, harvest, or collect data from the Service
                    without authorization; interfere with or disrupt the
                    Service or its infrastructure; upload malicious code or
                    harmful content; use the Service for any unlawful purpose;
                    or attempt to gain unauthorized access to any part of the
                    Service. Violation of these prohibitions may result in
                    immediate account suspension or termination.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    3. Content Standards
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    All content submitted to Muslim+, including business
                    descriptions, images, and certificates, must be accurate,
                    lawful, and not infringe on the rights of any third party.
                    Muslim+ reserves the right to remove any content that
                    violates these standards or is otherwise objectionable, at
                    our sole discretion.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="mb-2 text-lg font-semibold">
                    4. Reporting Violations
                  </h3>
                  <p className="leading-relaxed text-muted-foreground">
                    If you become aware of any violation of these Terms or
                    misuse of the Service, please report it to us at{" "}
                    <a
                      href="mailto:support@muslimplus.org"
                      className="text-primary hover:underline"
                    >
                      support@muslimplus.org
                    </a>
                    . We take all reports seriously and will investigate
                    promptly.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer Links */}
        <div className="mt-12 border-t pt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button variant="outline" size="lg" onClick={() => navigate("/policy")}>
            <Shield className="mr-2 h-4 w-4" />
            Privacy Policy
          </Button>
          <Button variant="outline" size="lg" onClick={() => navigate("/")}>
            <Home className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
